import { FC, RefObject, Suspense, useCallback, useMemo, useRef } from 'react';

import { RequirePermission } from '@gasbi/shared/common/providers/PermissionProvider/components/RequirePermission';
import {
  NavLinkTabsContainer,
  NavTabLink,
} from '@gasbi/shared/web/components/NavLinkTabs';
import { usePageContext } from '@gasbi/shared/web/providers/PageProvider';
import { Outlet, useNavigate, useOutletContext } from 'react-router-dom';

import { MeteringPointAct } from '@/features/meteringPointActs/types';
import { UserPermissions } from '@/features/permissions';
import { useShowEditButton } from '@/pages/MeteringPointActDetails/hooks/useShowEditButton';
import { CustomPortal } from '@/pages/MeteringPointActForm/views/Devices/components/CustomPortal';

import Button from '@mui/joy/Button';

export const ActFormView = {
  Information: '',
  Devices: 'devices',
  Equipments: 'equipments',
  Seals: 'seals',
} as const;

type DetailsNavigationProps = {
  meteringPointAct?: MeteringPointAct;
};

export type MeteringPointActDetailsOutletContext = {
  outletRef: RefObject<HTMLDivElement>;
  meteringPointAct?: MeteringPointAct;
  isLoading?: boolean;
};

export const useMeteringPointActDetailsOutletContext =
  (): MeteringPointActDetailsOutletContext =>
    useOutletContext<MeteringPointActDetailsOutletContext>();

export const MeteringPointActDetailsNavigation: FC<DetailsNavigationProps> = ({
  meteringPointAct,
}) => {
  const navigate = useNavigate();
  const { loading } = usePageContext();
  const outletRef = useRef(null);
  const showEditButton = useShowEditButton();

  const links: NavTabLink[] = useMemo(
    () => [
      { title: 'Загальна інформація', path: ActFormView.Information },
      { title: 'Прилади', path: ActFormView.Devices },
      { title: 'Обладнання', path: ActFormView.Equipments },
      { title: 'Пломби', path: ActFormView.Seals },
    ],
    [],
  );

  const handleEdit = useCallback(() => {
    if (!meteringPointAct) return;
    navigate(`../form/${meteringPointAct.id}`, { relative: 'path' });
  }, [navigate, meteringPointAct]);

  const outletContext = useMemo(
    () =>
      ({
        outletRef,
        meteringPointAct,
        isLoading: loading,
      }) satisfies MeteringPointActDetailsOutletContext,
    [meteringPointAct, loading],
  );

  return (
    <>
      <CustomPortal containerRef={outletRef}>
        {showEditButton && meteringPointAct && (
          <RequirePermission name={UserPermissions.PersonalAccountsActsManage}>
            <Button onClick={handleEdit}>Редагувати</Button>
          </RequirePermission>
        )}
      </CustomPortal>
      <NavLinkTabsContainer ref={outletRef} links={links} />
      <Suspense>
        <Outlet context={outletContext} />
      </Suspense>
    </>
  );
};
